import type React from "react"
import { useState, useRef, useEffect } from "react"
import { useFormContext } from "react-hook-form"
import { ChevronDown, X } from "lucide-react"
import "./MultiFancyDropdown.css"

interface Option {
  Id: string | number
  Name: string
}

interface MultiSelectDropdownProps {
  name: string
  label: string
  options: Option[]
  required?: boolean
  requiredMessage?: string
  placeholderText?: string
  defaultValue?: (string | number)[]
  validation?: {
    required?: string | boolean
    [key: string]: any
  }
  className?: string
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  disabled?: boolean
}

export default function MultiFancyDropdown({
  name,
  label,
  required = false,
  options,
  requiredMessage = "Please select at least one option",
  placeholderText = "Select options",
  validation = {},
  defaultValue = [],
  className = "",
  onChange,
  disabled = false,
  ...rest
}: MultiSelectDropdownProps) {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Watch the value from react-hook-form
  const selectedValues: (string | number)[] = watch(name) || []

  // Find the selected option objects
  const selectedOptions = options.filter((option) => selectedValues.includes(option.Id))

  useEffect(() => {
    // Set default value if provided
    if (defaultValue && defaultValue.length > 0) {
      setValue(name, defaultValue)
    }
  }, [defaultValue, name, setValue])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Register the field with react-hook-form
  const { ref, ...registerProps } = register(name, {
    required: required ? requiredMessage : false,
    ...validation,
  })

  const handleToggleOption = (option: Option) => {
    const currentValues = selectedValues || []
    const newValues = currentValues.includes(option.Id)
      ? currentValues.filter((id) => id !== option.Id)
      : [...currentValues, option.Id]

    setValue(name, newValues, { shouldValidate: true })

    // Call onChange if provided
    if (onChange) {
      // Create a synthetic event object similar to a multi-select element
      const syntheticEvent = {
        target: {
          name,
          value: newValues,
          selectedOptions: newValues.map((id) => {
            const opt = options.find((o) => o.Id === id)
            return { text: opt?.Name || "", value: id }
          }),
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>

      onChange(syntheticEvent)
    }
  }

  const handleRemoveTag = (optionId: string | number) => {
    const newValues = selectedValues.filter((id) => id !== optionId)
    setValue(name, newValues, { shouldValidate: true })

    // Call onChange if provided
    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: newValues,
          selectedOptions: newValues.map((id) => {
            const opt = options.find((o) => o.Id === id)
            return { text: opt?.Name || "", value: id }
          }),
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>

      onChange(syntheticEvent)
    }
  }

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholderText
    if (selectedValues.length === 1) return selectedOptions[0]?.Name
    return `${selectedValues.length} selected`
  }

  return (
    <div className={`multi-select-container ${className}`}>
      <label className="multi-select-label black">
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>

      <div className="multi-select-wrapper" ref={dropdownRef}>
        <div
          className={`multi-select-trigger ${isOpen ? "open" : ""} ${errors[name] ? "error" : ""} ${disabled ? "disabled" : ""}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className={`multi-select-placeholder ${selectedValues.length > 0 ? "has-value" : ""}`}>
            {getDisplayText()}
          </span>
          <ChevronDown className={`multi-select-arrow ${isOpen ? "rotated" : ""}`} />
        </div>

        {isOpen && !disabled && (
          <div className="multi-select-dropdown">
            {options.map((option) => (
              <div key={option.Id} className="multi-select-option" onClick={() => handleToggleOption(option)}>
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    className="option-checkbox"
                    checked={selectedValues.includes(option.Id)}
                    onChange={() => {}} // Handled by parent onClick
                    tabIndex={-1}
                  />
                  <span className="checkbox-label">{option.Name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedValues.length > 0 && (
        <div className="selected-tags">
          {selectedOptions.map((option) => (
            <div key={option.Id} className="selected-tag">
              <span className="tag-text">{option.Name}</span>
              {!disabled && (
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => handleRemoveTag(option.Id)}
                  aria-label={`Remove ${option.Name}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {errors[name] && <div className="error-message">{errors[name]?.message as string}</div>}

      {/* Hidden input for react-hook-form */}
      <input
        type="hidden"
        name={name}
        ref={ref}
        disabled={disabled}
        value={selectedValues.join(",")}
        {...registerProps}
        {...rest}
      />
    </div>
  )
}
